import React from "react";
import { Link } from "react-router-dom";
import { LogoLockup } from "./LogoMark";
import { MapPin, Mail, Clock, ArrowRight, Phone } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

export default function Footer() {
  const { t } = useLang();
  return (
    <footer data-testid="site-footer" className="bg-ink-950 text-white relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-24 pb-10 relative z-10">
        <div className="glass rounded-4xl p-10 md:p-16 mb-16 relative overflow-hidden">
          <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-steel/20 blur-3xl" />
          <div className="relative grid md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-8">
              <h3 className="font-serif text-3xl md:text-5xl leading-tight text-gradient" style={{ fontWeight: 500 }}>
                {t("Let's elevate your space.")}
              </h3>
              <p className="mt-4 text-white/55 max-w-lg">{t("Get a tailored, no-obligation quote and experience the North Peak standard — for your home or business.")}</p>
            </div>
            <div className="md:col-span-4 md:text-right">
              <Link to="/quote" data-testid="footer-quote-btn"
                className="btn-pill group inline-flex items-center gap-2 bg-white text-ink-950 px-7 py-4 text-sm font-600 hover:shadow-lg hover:shadow-white/20">
                {t("Request a Quote")} <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-12 gap-12 pb-14 border-b border-white/10">
          <div className="md:col-span-5">
            <LogoLockup dark />
            <p className="mt-6 text-white/80 text-sm font-500">{t("Professional Cleaning & Facility Services")}</p>
            <p className="mt-3 text-white/50 text-sm leading-relaxed max-w-sm">
              {t("Proudly serving Montreal and the Greater Montreal Area — homes, offices, medical facilities, retail, industrial properties and short-term rentals.")}
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/quote" data-testid="footer-portal-quote-btn" className="btn-pill bg-white text-ink-950 px-6 py-3 text-sm font-600 hover:shadow-lg hover:shadow-white/20">{t("Request a Quote")}</Link>
              <Link to="/portal" data-testid="footer-portal-btn" className="btn-pill glass text-white px-6 py-3 text-sm font-600 hover:bg-white/10">{t("Client Portal")}</Link>
            </div>
          </div>
          <div className="md:col-span-2">
            <h4 className="overline text-steel-light mb-5">{t("Company")}</h4>
            <ul className="space-y-3 text-sm text-white/60">
              <li><Link to="/about" className="hover:text-white transition-colors">{t("About")}</Link></li>
              <li><Link to="/residential" className="hover:text-white transition-colors">{t("Residential")}</Link></li>
              <li><Link to="/commercial" className="hover:text-white transition-colors">{t("Commercial")}</Link></li>
              <li><Link to="/services" className="hover:text-white transition-colors">{t("Services")}</Link></li>
            </ul>
          </div>
          <div className="md:col-span-2">
            <h4 className="overline text-steel-light mb-5">{t("Support")}</h4>
            <ul className="space-y-3 text-sm text-white/60">
              <li><Link to="/quote" className="hover:text-white transition-colors">{t("Request a Quote")}</Link></li>
              <li><Link to="/contact" className="hover:text-white transition-colors">{t("Contact")}</Link></li>
              <li><Link to="/portal" className="hover:text-white transition-colors">{t("Client Portal")}</Link></li>
            </ul>
          </div>
          <div className="md:col-span-3">
            <h4 className="overline text-steel-light mb-5">{t("Reach Us")}</h4>
            <ul className="space-y-4 text-sm text-white/60">
              <li className="flex gap-3"><MapPin className="h-4 w-4 mt-0.5 text-steel-light shrink-0" /> {t("Serving Montreal & the Greater Montreal Area, Quebec")}</li>
              <li className="flex gap-3"><Phone className="h-4 w-4 mt-0.5 text-steel-light shrink-0" /> <a href="tel:+14389460924" data-testid="footer-phone" className="hover:text-white transition-colors">438-946-0924</a></li>
              <li className="flex gap-3"><Mail className="h-4 w-4 mt-0.5 text-steel-light shrink-0" /> <a href="mailto:info@northpeakfs.ca" className="hover:text-white transition-colors">info@northpeakfs.ca</a></li>
              <li className="flex gap-3"><Clock className="h-4 w-4 mt-0.5 text-steel-light shrink-0" /> {t("Mon–Sat · 8:00–17:00")}</li>
              <li><a href="https://share.google/vBLi2F0e9RXp859aF" target="_blank" rel="noopener noreferrer" data-testid="footer-google-link" className="text-steel-light hover:text-white transition-colors">{t("View us on Google")}</a></li>
            </ul>
          </div>
        </div>

        <div className="flex flex-col md:flex-row justify-between items-center gap-4 pt-8 text-xs text-white/35">
          <p>{t("© 2026 North Peak Facility Services. All Rights Reserved.")}</p>
          <div className="flex gap-6">
            <span className="hover:text-white/60 transition-colors cursor-pointer">{t("Privacy")}</span>
            <span className="hover:text-white/60 transition-colors cursor-pointer">{t("Terms")}</span>
            <span className="hover:text-white/60 transition-colors cursor-pointer">{t("Accessibility")}</span>
          </div>
        </div>
      </div>

      <div className="select-none pointer-events-none">
        <h2 className="font-serif text-[19vw] leading-none text-white/[0.03] text-center -mb-[3vw] tracking-tight">
          NORTH&nbsp;PEAK
        </h2>
      </div>
    </footer>
  );
}
